import { Injectable } from '@angular/core';
import { CoreProvider } from '../core.provider';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { TeamModelDto } from '../api/models';
import { updateLocalFromCloud } from './storage.service';

export type Team = TeamModelDto & {
  localLastUpdate: number|null;
  pendingDelete: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class TeamsService {
  private readonly TEAMS_STORAGE_KEY = 'teams';
  private core?: CoreProvider;

  private teamsSubject = new BehaviorSubject<Team[]>([]);
  public get teams$(): Observable<Team[]> {
    return this.teamsSubject.asObservable();
  }

  constructor() {}

  async initCore(core: CoreProvider) {
    this.core = core;
    this.getTeams();
  }

  private generateLocalId(): string {
    return crypto.randomUUID();
  }

  async getTeams(): Promise<Team[]> {
    const teamsJson = await this.core!.storage.getItem(this.TEAMS_STORAGE_KEY);
    const teams = teamsJson ? (JSON.parse(teamsJson) as Team[]).sort((a, b) => a.name.localeCompare(b.name)) : [];
    this.teamsSubject.next(teams);
    return teams;
  }

  async saveTeam(teamName: string): Promise<Team> {
    const teams = await this.getTeams();
    const newTeam: Team = {
      localId: this.generateLocalId(),
      localLastUpdate: Date.now(),
      pendingDelete: false,
      name: teamName
    };
    teams.push(newTeam);
    this.updateTeams(teams);
    return newTeam;
  }

  async updateTeams(teams: Team[], preventCloudUpdate: boolean = false): Promise<void> {
    await this.core!.storage.setItem(this.TEAMS_STORAGE_KEY, JSON.stringify(teams));
    this.teamsSubject.next(teams);
    if (!preventCloudUpdate) await this.syncToCloud();
  }

  async syncToCloud(): Promise<void> {
    const syncedTeams = await this.core!.storage.syncToCloud(await this.getTeams(), {
      getall: () => firstValueFrom(this.core!.api.user.userControllerGetTeams()),
      create: (team) => firstValueFrom(this.core!.api.user.userControllerCreateTeam({ body: team })),
      delete: (team) => firstValueFrom(this.core!.api.user.userControllerDeleteTeam({ uuid: team.uuid! })),
      update: (team) => firstValueFrom(this.core!.api.user.userControllerUpdateTeam({ uuid: team.uuid!, body: team })),
    }, cloudTeam => ({
      ...cloudTeam,
      localLastUpdate: null,
      pendingDelete: false
    } as Team));
    this.updateTeams(syncedTeams, true);
  }

}

